import { useRef, useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

import useAppStore from "../../store/useAppStore";
import useLayoutStore from "../../store/useLayoutStore";

const EditDeviceModal = ({ device, onClose }) => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme);
    const updateDeviceData = useAppStore.getState().updateDeviceData;
    const deviceGroups = Object.keys(useAppStore.getState().deviceGroupsState);

    const modalContainerRef = useRef(null);
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [addingNewItemId, setAddingNewItemId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const devicesImg = useMemo(() => {
        const baseImgUrl = import.meta.env.VITE_API_IMAGES_URL;
        return `${baseImgUrl}/devices/${device.image}`;
    }, []);

    const deviceRooms = useMemo(() => {
        const devicesData = useAppStore.getState().devicesData;
        if (!devicesData) return [];
        const rooms = new Set();
        Object.values(devicesData).forEach(device => {
            if (device.room) {
                rooms.add(device.room);
            }
        });
        return Array.from(rooms);
    }, []);

    const employeesList = useMemo(() => {
        const employeesData = useAppStore.getState().employeesData;
        if (!employeesData) return [];
        const baseImgUrl = import.meta.env.VITE_API_IMAGES_URL;
        return Object.entries(employeesData).map(([userId, employee]) => ({
            userId,
            fullName: `${employee.ime} ${employee.prezime}`,
            image: `${baseImgUrl}/employees/${employee.image}`,
            ime: employee.ime,
            prezime: employee.prezime
        }));
    }, []);

    const groupRef = useRef(device.group || 'Bez grupe');
    const roomRef = useRef(device.room || '');
    const employeeRef = useRef(device.employee ? employeesList.find(employee => employee.userId === device.employee) : null);

    const handleChangeDeviceGroup = (e) => {
        groupRef.current = e;
    }

    const handleChangeDeviceRoom = (e) => {
        roomRef.current = e;
    }

    const handleChangeEmployee = (employee) => {
        employeeRef.current = employee;
    }

    const handleConfirmChanges = async () => {
        // If user typed a new room/group but did not click checkmark,
        // auto-apply that value before validating and submitting the form.
        if (addingNewItemId) {
            const pendingInput = modalContainerRef.current?.querySelector('.dv-edit-dropdown-input');
            const pendingValue = pendingInput?.value?.trim();

            if (pendingValue) {
                if (addingNewItemId === 'room') {
                    roomRef.current = pendingValue;
                } else if (addingNewItemId === 'group') {
                    groupRef.current = pendingValue;
                }
            }

            setAddingNewItemId(null);
        }

        const newRoom = roomRef.current;
        const newGroup = groupRef.current;
        const newEmployee = employeeRef.current;

        if (!newRoom || !newGroup) {
            return;
        }

        const currentRoom = device.room || '';
        const currentGroup = device.group || 'Bez grupe';
        const currentEmployeeId = device.employee?.userId ?? device.employee ?? null;
        const newEmployeeId = newEmployee ? (newEmployee.userId ?? newEmployee) : null;

        if (
            newRoom === currentRoom &&
            newGroup === currentGroup &&
            newEmployeeId === currentEmployeeId
        ) {
            return;
        }

        const deviceData = {
            deviceId: device._id,
            room: newRoom,
            group: newGroup,
            employee: newEmployee ? newEmployee.userId : null
        }
        setIsSubmitting(true);
        try {
            const response = await updateDeviceData(deviceData);
            if (response) {
                onClose();
            }
        } finally {
            setIsSubmitting(false);
        }
    }

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (modalContainerRef.current && modalContainerRef.current.contains(e.target)) {
                return;
            }
            onClose();
        };
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, [onClose]);

    const modalContent = (
        <>
            <motion.div
                className="dv-edit-modal-container"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, delay: 0.14 }}
                transition={{ duration: 0.14, ease: "easeInOut" }}
                style={{ pointerEvents: 'none' }}
            >
                <motion.div
                    ref={modalContainerRef}
                    className={`dv-edit-modal ${isDarkTheme ? "dark-dv-edit-modal" : "white-dv-edit-modal"}`}
                    initial={{ opacity: 0, scale: 0.4 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.4 }}
                    transition={{ duration: 0.14, ease: "easeInOut" }}
                    style={{ pointerEvents: 'auto' }}
                // onClick={(e) => e.stopPropagation()}
                >
                    <button
                        className={`dv-edit-modal-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`}
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        <svg
                            width="28"
                            height="28"
                            viewBox="0 0 28 28"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ display: "block", margin: "auto" }}
                        >
                            <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                            <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                        </svg>
                    </button>
                    <h3>
                        <img src='/sidebar/Podesavanja.svg' />
                        Podešavanje uređaja
                    </h3>
                    <div className="dv-edit-description">
                        Promenite lokaciju uređaja, dodelite ga grupi i, ako je dostupno, povežite sa zaposlenim.
                    </div>
                    <div className="dv-edit-device-header">
                        <img src={devicesImg} crossOrigin="use-credentials" />
                        <h4>{device.device}</h4>
                    </div>
                    <div className="dv-edit-settings-container">
                        <div className="dv-edit-field">
                            <label>Lokacija uredjaja:</label>
                            <Dropdown
                                dropdownId="room"
                                openDropdownId={openDropdownId}
                                setOpenDropdownId={setOpenDropdownId}
                                addingNewItemId={addingNewItemId}
                                setAddingNewItemId={setAddingNewItemId}
                                valueRef={roomRef}
                                onChange={handleChangeDeviceRoom}
                                dropdownItems={deviceRooms}
                                label="Lokacija uredjaja:"
                            />
                        </div>
                        <div className="dv-edit-field">
                            <label>Grupa:</label>
                            <Dropdown
                                dropdownId="group"
                                openDropdownId={openDropdownId}
                                setOpenDropdownId={setOpenDropdownId}
                                addingNewItemId={addingNewItemId}
                                setAddingNewItemId={setAddingNewItemId}
                                valueRef={groupRef}
                                onChange={handleChangeDeviceGroup}
                                dropdownItems={deviceGroups}
                                label="Grupa:"
                            />
                        </div>
                        {['S26R2'].includes(device.device) && (
                            <div className="dv-edit-field">
                                <label>Od zaposlenog:</label>
                                <EmployeeDropdown
                                    dropdownId="employee"
                                    openDropdownId={openDropdownId}
                                    setOpenDropdownId={setOpenDropdownId}
                                    valueRef={employeeRef}
                                    onChange={handleChangeEmployee}
                                    dropdownItems={employeesList}
                                    label="Od zaposlenog:"
                                />
                            </div>
                        )}
                        <button
                            className={`dv-edit-save-button ${isDarkTheme ? "dark-big-btn" : ""}`}
                            onClick={handleConfirmChanges}
                            disabled={isSubmitting}
                        >
                            Potvrdi
                        </button>
                    </div>
                </motion.div>
            </motion.div>
            <motion.div
                className={`profile-modal-bg-wrapper ${isDarkTheme ? "dark-profile-modal-bg-wrapper" : "white-profile-modal-bg-wrapper"}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 1 }}
                transition={{ duration: 0.14, ease: "easeInOut" }}
                style={{ pointerEvents: 'auto' }}
            >

            </motion.div>
        </>
    );

    return createPortal(modalContent, document.body);
}

const Dropdown = ({ dropdownId, openDropdownId, setOpenDropdownId, addingNewItemId, setAddingNewItemId, valueRef, onChange, dropdownItems, label }) => {
    const inputRef = useRef(null);

    const isLocationDropdown = label === 'Lokacija uredjaja:';
    const newItemLabel = isLocationDropdown ? 'Unesite novu lokaciju' : 'Unesite novu grupu';
    const isOpen = openDropdownId === dropdownId;
    const isAddingNewItem = addingNewItemId === dropdownId;

    const handleSelectValueChange = (e) => {
        onChange(e);
        setAddingNewItemId(null);
    }

    const handleToggle = () => {
        if (isAddingNewItem) return;
        setOpenDropdownId(isOpen ? null : dropdownId);
    }

    const handleNewItemClick = (e) => {
        e.stopPropagation();
        setAddingNewItemId(dropdownId);
        setOpenDropdownId(null);
    }

    const handleCancelNewItem = () => {
        setAddingNewItemId(null);
    }

    const handleConfirmNewItem = () => {
        if (inputRef.current && inputRef.current.value.trim()) {
            const newValue = inputRef.current.value.trim();
            onChange(newValue);
            setAddingNewItemId(null);
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            handleCancelNewItem();
        } else if (e.key === 'Enter') {
            e.preventDefault();
            handleConfirmNewItem();
        }
    }


    return (
        <div
            className={`dv-edit-dropdown${isAddingNewItem ? " dv-edit-dropdown--adding" : ""}`}
            onClick={handleToggle}
        >
            <div className="dv-edit-dropdown-value">
                <img
                    src={isLocationDropdown ? "./sidebar/Office.svg" : "./views/Grupa.svg"}
                    alt=""
                    // className="dv-edit-dropdown-icon"
                    className={`dv-edit-dropdown-icon ${isLocationDropdown ? "" : "dv-edit-dropdown-icon-group"}`}
                />
                <AnimatePresence mode="wait" >
                    {isAddingNewItem ? (
                        <motion.input
                            ref={inputRef}
                            key="dropdown-input"
                            type="text"
                            className="dv-edit-dropdown-input"
                            autoFocus
                            placeholder={newItemLabel}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={handleKeyDown}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            transition={{ duration: 0.14, ease: "easeOut" }}
                        />
                    ) : (
                        <motion.span
                            key={`dropdown-value-${String(valueRef.current)}`}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            transition={{ duration: 0.14, ease: "easeOut" }}
                        >
                            {valueRef.current}
                        </motion.span>
                    )}
                </AnimatePresence>
            </div>
            <div className="arrow-container">
                <AnimatePresence mode="wait">
                    {isAddingNewItem ? (
                        <div
                            key="confirm-cancel-icons"
                            style={{ display: "flex", gap: 4, alignItems: "center" }}
                        >
                            <motion.svg
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancelNewItem();
                                }}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0 }}
                                transition={{ duration: 0.14, ease: "easeOut" }}
                            >
                                <path
                                    d="M6 6l12 12M18 6l-12 12"
                                    fill="none"
                                    stroke="#08293e"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </motion.svg>
                            <motion.svg
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleConfirmNewItem();
                                }}
                                style={{ display: "block" }}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0 }}
                                transition={{ duration: 0.19, ease: "easeOut", delay: 0.14 }}
                            >
                                <path
                                    d="M5 12l5 5 9-9"
                                    fill="none"
                                    stroke="#08293e"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </motion.svg>
                        </div>
                    ) : (
                        <div className="dv-edit-dropdown-icon-container">
                            <motion.div
                                key="dropdown-arrow"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1, rotate: isOpen ? 180 : 0 }}
                                exit={{ scale: 0 }}
                                transition={{ duration: 0.19, ease: "easeOut" }}
                                style={{ transformOrigin: "center" }}
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    width="39"
                                    height="39"
                                    style={{ display: "block" }}
                                >
                                    <path
                                        d="M7 10l5 5 5-5"
                                        fill="none"
                                        stroke="#08293e"
                                        strokeWidth="1.9"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>
            </div>
            <AnimatePresence mode="wait">
                {isOpen && (
                    <motion.div
                        className="dv-edit-dropdown-list"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.14, ease: "easeOut" }}
                    >
                        <div
                            className="dv-edit-dropdown-item dv-edit-dropdown-new-item"
                            onClick={handleNewItemClick}
                        >
                            <span>{newItemLabel}</span>
                            <svg
                                viewBox="0 0 24 24"
                                width="20"
                                height="20"
                                style={{ display: "block" }}
                            >
                                <path
                                    d="M12 5v14M5 12h14"
                                    fill="none"
                                    stroke="#08293e"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        {dropdownItems.map((item, i) => (
                            <div
                                key={i}
                                className="dv-edit-dropdown-item"
                                onClick={() => {
                                    handleSelectValueChange(item);
                                }}
                            >
                                {item}
                            </div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

const EmployeeDropdown = ({ dropdownId, openDropdownId, setOpenDropdownId, valueRef, onChange, dropdownItems }) => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme);
    const dropdownMaxHeight = useLayoutStore(state => state.dropdownMaxHeight);
    const setActiveDropdownElement = useLayoutStore(state => state.setActiveDropdownElement);
    const calculateDropdownMaxHeight = useLayoutStore(state => state.calculateDropdownMaxHeight);
    const dropdownRef = useRef(null);
    const isOpen = openDropdownId === dropdownId;

    const handleSelectValueChange = (employee) => {
        onChange(employee);
    }

    const handleToggle = () => {
        setOpenDropdownId(isOpen ? null : dropdownId);
    }

    // Postavi dropdown element u store kada se otvori i izračunaj maxHeight
    useEffect(() => {
        if (isOpen && dropdownRef.current) {
            setActiveDropdownElement(dropdownRef.current);
            calculateDropdownMaxHeight();
        } else {
            setActiveDropdownElement(null);
        }
    }, [isOpen, setActiveDropdownElement, calculateDropdownMaxHeight]);

    const selectedEmployee = valueRef.current;
    const displayValue = selectedEmployee ? selectedEmployee.fullName : '';
    const displayImage = selectedEmployee ? selectedEmployee.image : null;

    return (
        <div
            ref={dropdownRef}
            className="dv-edit-dropdown"
            onClick={handleToggle}
        >
            <div className="dv-edit-dropdown-value">
                <AnimatePresence mode="wait" initial={false}>
                    {displayImage ? (
                        <motion.img
                            key={`employee-image-${String(displayImage)}`}
                            src={displayImage}
                            alt=""
                            className="dv-edit-dropdown-icon dv-edit-dropdown-employee-icon"
                            crossOrigin="use-credentials"
                            onError={(e) => {
                                e.target.src = "./views/login.svg";
                            }}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            transition={{ duration: 0.14, ease: "easeOut" }}
                        />
                    ) : (
                        <motion.img
                            key="employee-image-default"
                            src="./views/NijeTu.svg"
                            alt=""
                            // className="dv-edit-dropdown-employee-icon"
                            className={`dv-edit-dropdown-employee-icon ${isDarkTheme ? "dark-filter" : ""}`}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            transition={{ duration: 0.14, ease: "easeOut" }}
                        />
                    )}
                </AnimatePresence>
                <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                        key={`employee-value-${String(displayValue || "none")}`}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{ duration: 0.14, ease: "easeOut" }}
                    >
                        {displayValue || 'Bez zaposlenog'}
                    </motion.span>
                </AnimatePresence>
            </div>
            <div
                className="arrow-container"
            >
                <motion.div
                    key="dropdown-arrow"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1, rotate: isOpen ? 180 : 0 }}
                    exit={{ scale: 0 }}
                    transition={{ duration: 0.19, ease: "easeOut" }}
                    style={{ transformOrigin: "center" }}
                >
                    <svg
                        viewBox="0 0 24 24"
                        width="39"
                        height="39"
                        style={{ display: "block" }}
                    >
                        <path
                            d="M7 10l5 5 5-5"
                            fill="none"
                            stroke="#08293e"
                            strokeWidth="1.9"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </motion.div>
            </div>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="dv-edit-dropdown-list"
                        style={{ maxHeight: `${Math.min(194, dropdownMaxHeight)}px` }}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.14, ease: "easeOut" }}
                    >
                        <div
                            className="dv-edit-dropdown-item dv-edit-dropdown-employee-item"
                            onClick={() => {
                                handleSelectValueChange(null);
                            }}
                        >
                            <img
                                src="./views/NijeTu.svg"
                                alt=""
                                className="dv-edit-dropdown-employee-item-img"
                            />
                            <span>Bez zaposlenog</span>
                        </div>
                        {dropdownItems.map((employee, i) => (
                            <div
                                key={employee.userId || i}
                                className="dv-edit-dropdown-item dv-edit-dropdown-employee-item"
                                onClick={() => {
                                    handleSelectValueChange(employee);
                                }}
                            >
                                <img
                                    src={employee.image || "./views/login.svg"}
                                    alt=""
                                    className="dv-edit-dropdown-employee-item-img"
                                    crossOrigin="use-credentials"
                                    onError={(e) => {
                                        e.target.src = "./views/login.svg";
                                    }}
                                />
                                <span>{employee.fullName}</span>
                            </div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export { Dropdown };
export default EditDeviceModal;
